<template>
  <div class="na-history-section">
    <div class="na-history-header" @click="$emit('toggle-collapse')">
      <div class="na-history-title">
        <font-awesome-icon icon="fa fa-sliders" class="na-history-icon" />
        <span>Gráfico</span>
      </div>
      <div class="na-history-toggle">
        <span v-if="metricLabel" class="na-history-metric-label">{{ metricLabel }}</span>
        <font-awesome-icon
          icon="fa fa-chevron-down"
          :class="['na-arrow', { collapsed: collapsed }]"
        />
      </div>
    </div>

    <div v-show="!collapsed" class="na-history-body">
      <div class="na-chart-container">
        <canvas ref="chartCanvas" :key="chartKey" width="320" height="175"></canvas>
      </div>
      <div class="na-chart-legend" v-if="showMinMax">
        <span class="na-legend-item"><span class="na-legend-dot" style="background:#5CC75D"></span>máxima</span>
        <span class="na-legend-item"><span class="na-legend-dot" style="background:#B0B0B0"></span>média</span>
        <span class="na-legend-item"><span class="na-legend-dot" style="background:#BBD1FF"></span>mínimo</span>
      </div>
    </div>
  </div>
</template>

<script>
/* global Chart */

export default {
  name: "NewAsideHistoryChart",
  props: {
    hours: { type: Array, default: () => [] },
    metricField: { type: String, default: "temperature" },
    metricLabel: { type: String, default: "Vento" },
    metricColor: { type: String, default: "#006035" },
    chartType: { type: String, default: "line" },
    collapsed: { type: Boolean, default: false },
    showMinMax: { type: Boolean, default: false },
    extraSeries: { type: Array, default: null },
    /**
     * Linhas horizontais de referência: [{ value, color, label }].
     * Usado pelas faixas de cheia das estações de terceiros. Vazio = nenhuma linha,
     * então o aside de dispositivo próprio não é afetado.
     */
    referenceLines: { type: Array, default: () => [] },
    /**
     * Força o eixo Y a começar em zero.
     *
     * Só para grandezas em que o zero é referência real e a escala relativa importa:
     * profundidade/volume de caixa d'água, onde "4,96 m" precisa ser lido contra os 5,50 m
     * de altura total. Sem isso o Chart.js recorta o eixo na faixa dos dados (começou em
     * 4,90) e uma variação de 60 cm ocupa o gráfico inteiro, parecendo esvaziamento brusco.
     *
     * NÃO é o padrão: em temperatura, umidade e vento o zero não é referência, e ancorar
     * neles achataria a curva a ponto de esconder a variação que se quer ver.
     */
    zeroBased: { type: Boolean, default: false },
  },
  emits: ["toggle-collapse"],
  data() {
    return {
      chartKey: 0,
    };
  },
  watch: {
    hours: {
      handler() {
        this.refreshChart();
      },
      deep: true,
    },
    metricField() {
      this.refreshChart();
    },
    collapsed(val) {
      if (!val) {
        this.refreshChart();
      }
    },
  },
  mounted() {
    if (!this.collapsed) {
      this.$nextTick(() => this.renderChart());
    }
  },
  methods: {
    refreshChart() {
      // Increment key to force Vue to destroy the old canvas and create
      // a brand-new one. This guarantees a fresh canvas context on every
      // browser/webview, eliminating stale-context errors (Safari, Android).
      this.chartKey++;
      this.$nextTick(() => this.renderChart());
    },
    renderChart() {
      if (!this.$refs.chartCanvas || !this.hours.length) return;

      const labels = [];
      const dataAvg = [];
      const dataMin = [];
      const dataMax = [];

      for (const h of this.hours) {
        const dt = h.dateTime || h.data;
        if (dt) {
          labels.push(new Date(dt).toLocaleTimeString("pt-BR").substring(0, 5));
        }
        if (this.showMinMax) {
          dataAvg.push(h.avg ?? h[this.metricField] ?? null);
          dataMin.push(h.min ?? null);
          dataMax.push(h.max ?? null);
        } else {
          dataAvg.push(h[this.metricField] ?? null);
        }
      }

      const ctx = this.$refs.chartCanvas.getContext("2d");
      const gradientAvg = ctx.createLinearGradient(0, 0, 0, 175);
      gradientAvg.addColorStop(0, this.metricColor + "44");
      gradientAvg.addColorStop(1, this.metricColor + "05");

      let datasets;

      if (this.showMinMax) {
        const gradientMax = ctx.createLinearGradient(0, 0, 0, 175);
        gradientMax.addColorStop(0, "rgba(92, 199, 93, 0.15)");
        gradientMax.addColorStop(1, "rgba(92, 199, 93, 0.02)");

        const gradientMin = ctx.createLinearGradient(0, 0, 0, 175);
        gradientMin.addColorStop(0, "rgba(187, 209, 255, 0.25)");
        gradientMin.addColorStop(1, "rgba(187, 209, 255, 0.05)");

        datasets = [
          {
            fill: true,
            borderColor: "#5CC75D",
            backgroundColor: gradientMax,
            label: "máxima",
            data: dataMax,
            borderWidth: 1.5,
            pointRadius: 1,
          },
          {
            fill: true,
            borderColor: "#B0B0B0",
            backgroundColor: gradientAvg,
            label: "média",
            data: dataAvg,
            borderWidth: 2,
            pointRadius: 2,
          },
          {
            fill: true,
            borderColor: "#BBD1FF",
            backgroundColor: gradientMin,
            label: "mínimo",
            data: dataMin,
            borderWidth: 1.5,
            pointRadius: 1,
          },
        ];
      } else if (this.extraSeries && this.extraSeries.length) {
        datasets = this.extraSeries.map(s => {
          const sData = this.hours.map(h => h[s.field] ?? null);
          const grad = ctx.createLinearGradient(0, 0, 0, 175);
          grad.addColorStop(0, s.color + "44");
          grad.addColorStop(1, s.color + "05");
          return {
            fill: true,
            borderColor: s.color,
            backgroundColor: grad,
            label: s.label,
            data: sData,
            borderWidth: 2,
            pointRadius: 2,
          };
        });
      } else {
        datasets = [
          {
            fill: true,
            borderColor: this.metricColor,
            backgroundColor: gradientAvg,
            label: this.metricLabel,
            data: dataAvg,
          },
        ];
      }

      // Linhas de referência (faixas de cheia). Desenhadas por um plugin inline em vez do
      // chartjs-plugin-annotation para não adicionar dependência — são apenas retas
      // horizontais com rótulo.
      const referenceLines = (this.referenceLines || []).filter(
        (l) => l && l.value != null && !isNaN(Number(l.value))
      );

      const referenceLinePlugin = {
        afterDatasetsDraw: (chart) => {
          if (!referenceLines.length) return;

          const yAxis = chart.scales["y-axis-0"] || Object.values(chart.scales)[0];
          const area = chart.chartArea;
          if (!yAxis || !area) return;

          const c = chart.ctx;
          c.save();

          referenceLines.forEach((linha) => {
            const y = yAxis.getPixelForValue(Number(linha.value));
            // Fora da área visível: não desenha (evita risco colado na borda).
            if (y < area.top || y > area.bottom) return;

            c.beginPath();
            c.moveTo(area.left, y);
            c.lineTo(area.right, y);
            c.lineWidth = 2;
            c.strokeStyle = linha.color || "#999";
            c.stroke();

            if (linha.label) {
              c.font = "600 9px system-ui, sans-serif";
              const texto = linha.label;
              const larg = c.measureText(texto).width + 8;
              const alt = 11;

              // Acima da linha por padrão; abaixo quando não couber (linha perto do topo),
              // senão a etiqueta seria cortada pela borda do gráfico.
              const cabeAcima = y - alt - 1 >= area.top;
              const topo = cabeAcima ? y - alt - 1 : y + 1;

              c.fillStyle = linha.color || "#999";
              c.fillRect(area.right - larg, topo, larg, alt);
              c.fillStyle = "#fff";
              c.textAlign = "center";
              c.textBaseline = "middle";
              c.fillText(texto, area.right - larg / 2, topo + alt / 2);
            }
          });

          c.restore();
        },
      };

      // A escala precisa alcançar as linhas: sem isso, uma referência acima do maior
      // valor lido ficaria fora do gráfico e o usuário não veria o quanto falta para
      // atingi-la — que é justamente a informação útil.
      const valoresSerie = [...dataAvg, ...(this.showMinMax ? [...dataMax, ...dataMin] : [])]
        .filter((v) => v != null && !isNaN(Number(v)))
        .map(Number);
      const valoresRef = referenceLines.map((l) => Number(l.value));

      let yMin, yMax, yStep;
      if (this.zeroBased && valoresSerie.length) {
        // Base fixa em zero; o topo continua acompanhando os dados, com folga.
        //
        // Divide por 6, não por 5 como o ramo abaixo: partindo do zero o intervalo é bem
        // maior, e com 5 o passo arredondava para cima demais (uma caixa de 5,5 m ganhava
        // eixo 0-2-4-6, grosseiro demais para ler o nível). Com 6 sai 0..6 de 1 em 1.
        const max = Math.max(...valoresSerie, ...valoresRef, 0);
        const bruto = (max * 1.08 || 1) / 6;
        const mag = Math.pow(10, Math.floor(Math.log10(bruto || 1)));
        const norm = bruto / mag;
        yStep = (norm >= 5 ? 10 : norm >= 2 ? 5 : norm >= 1 ? 2 : 1) * mag;

        yMin = 0;
        yMax = Math.ceil((max * 1.08) / yStep) * yStep;
      } else if (referenceLines.length && valoresSerie.length) {
        const min = Math.min(...valoresSerie, ...valoresRef);
        const max = Math.max(...valoresSerie, ...valoresRef);
        const folga = (max - min || 1) * 0.08;

        // Arredonda para um passo "redondo" (1, 2, 5, 10...): sem isso os limites ficam
        // fracionários e o Chart.js gera rótulos quase idênticos que se sobrepõem
        // (ex.: 52,2 colado em 52).
        const bruto = (max + folga - (min - folga)) / 5;
        const mag = Math.pow(10, Math.floor(Math.log10(bruto || 1)));
        const norm = bruto / mag;
        yStep = (norm >= 5 ? 10 : norm >= 2 ? 5 : norm >= 1 ? 2 : 1) * mag;

        yMin = Math.floor((min - folga) / yStep) * yStep;
        yMax = Math.ceil((max + folga) / yStep) * yStep;
      }

      try {
        new Chart(this.$refs.chartCanvas, {
          plugins: [referenceLinePlugin],
          type: this.chartType,
          data: {
            labels: labels,
            datasets: datasets,
          },
          options: {
            animation: { duration: 300 },
            responsive: true,
            maintainAspectRatio: false,
            legend: { display: false },
            tooltips: {
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              titleFontColor: "#333",
              bodyFontColor: "#333",
              borderColor: "#ddd",
              borderWidth: 1,
              titleFontSize: 11,
              bodyFontSize: 12,
              xPadding: 10,
              yPadding: 10,
              cornerRadius: 8,
              mode: "index",
              intersect: false,
            },
            scales: {
              yAxes: [
                {
                  ticks: {
                    beginAtZero: this.chartType === "bar",
                    fontColor: "#999",
                    fontSize: 10,
                    // Só definidos quando há linhas de referência; caso contrário o
                    // Chart.js segue calculando a escala como antes.
                    ...(yMin != null ? { min: yMin, max: yMax, stepSize: yStep } : {}),
                  },
                  gridLines: { color: "#e8e8e8" },
                },
              ],
              xAxes: [
                {
                  ticks: {
                    fontColor: "#999",
                    fontSize: 9,
                    maxRotation: 0,
                  },
                  gridLines: { display: false },
                },
              ],
            },
          },
        });
      } catch (ex) {
        // Chart creation may fail if canvas not ready
      }
    },
  },
};
</script>

<style scoped>
.na-history-section {
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  margin-top: 8px;
}

.na-history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  cursor: pointer;
  user-select: none;
}

.na-history-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  color: #333;
}

.na-history-icon {
  color: #006035;
  font-size: 14px;
}

.na-history-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
}

.na-history-metric-label {
  font-size: 12px;
  color: #666;
}

.na-arrow {
  font-size: 12px;
  color: #666;
  transition: transform 0.3s;
}

.na-arrow.collapsed {
  transform: rotate(-90deg);
}

.na-history-body {
  padding: 0 12px 12px;
}

.na-chart-container {
  position: relative;
  height: 175px;
}

.na-chart-legend {
  display: flex;
  justify-content: center;
  gap: 14px;
  margin-top: 6px;
  font-size: 10px;
  color: #666;
}

.na-legend-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.na-legend-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}

</style>
